import { ProjectGlobalData, ReportMetadata } from '../../types';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DATENPFLEGE IM PROJEKT-HUB — die reine Logik
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Anlass (Betreiber 12.09.2026): Stammdaten bleiben liegen. Kein Enddatum,
 * keine Beschreibung, keine Adresse, keine Kategorie, kein Projektbild —
 * und niemand merkt es, bis das Referenzblatt oder die Uebersicht leer ist.
 *
 * Beim Oeffnen eines Projekts fragt der Hub deshalb nach, was fehlt. Diese
 * Datei sagt nur, WAS fehlt (`findeDatenluecken`) und ob der Nutzer die
 * Frage in dieser Sitzung schon weggeklickt hat. Die Oberflaeche steckt in
 * ProjectDataHygieneDialog.
 *
 * Regeln:
 *  · Abgeschlossene Projekte: nie fragen.
 *  · SiFa-Dauerbetreuung: kein Ende, keine Kategorie, kein Bild-Countdown —
 *    dort gibt es weder Bauende noch Bau-Kategorie (docs/sifa-betreuung-plan.md).
 *  · Ende fehlt, endet in ≤ 7 Tagen oder ist ueberschritten → Rueckfrage.
 *    Eine Bestaetigung gilt fuer GENAU dieses Datum und 7 Tage lang.
 *  · Projektbild: erst 4 Wochen vor dem Ende Pflicht. Ohne Enddatum laesst
 *    sich das nicht beurteilen — dann keine Bildluecke, das Ende ist ohnehin
 *    schon eine.
 */

export type LueckenArt =
    | 'ende_fehlt'
    | 'ende_naht'
    | 'ende_ueberschritten'
    | 'beschreibung'
    | 'adresse'
    | 'kategorie'
    | 'bild';

export interface DatenLuecke {
    art: LueckenArt;
    /** Nur bei ende_naht / ende_ueberschritten: Tage bis zum (bzw. seit dem) Ende. */
    tage?: number;
}

export const ENDE_WARNUNG_TAGE = 7;
export const BILD_WARNUNG_TAGE = 28;
export const BESTAETIGUNG_GUELTIG_TAGE = 7;

type HygieneMetadata = Partial<Pick<ReportMetadata,
    'projectType' | 'projectAddress' | 'projectDescription' | 'projectCategory' | 'category'>>;
type HygieneGlobalData = Pick<ProjectGlobalData, 'status' | 'endDate' | 'endDateConfirmation'>;

export interface HygieneEingabe {
    metadata: HygieneMetadata;
    globalData: HygieneGlobalData | undefined;
    /** YYYY-MM-DD, lokaler Kalendertag. */
    heute: string;
    /**
     * Liegt in `project_media` mindestens ein Bild? `null` = unbekannt (noch
     * nicht geladen oder Tabelle fehlt) — dann wird keine Bildluecke gemeldet.
     */
    bildVorhanden: boolean | null;
}

/** Lokaler Kalendertag als YYYY-MM-DD — NICHT toISOString (das ist UTC, abends einen Tag daneben). */
export const heuteLokal = (d: Date = new Date()): string => {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** YYYY-MM-DD oder DD.MM.YYYY → UTC-Mitternacht in ms; null bei allem anderen. */
const tagInMs = (wert: string | undefined | null): number | null => {
    const s = (wert || '').trim();
    if (!s) return null;
    let y: number, m: number, d: number;
    let match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) { y = +match[1]; m = +match[2]; d = +match[3]; }
    else {
        match = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
        if (!match) return null;
        d = +match[1]; m = +match[2]; y = +match[3];
    }
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;
    const ms = Date.UTC(y, m - 1, d);
    return Number.isNaN(ms) ? null : ms;
};

/** Ganze Tage von `heute` bis `datum` (negativ = liegt zurueck); null bei ungueltigem Datum. */
export const tageBis = (heute: string, datum: string | undefined | null): number | null => {
    const a = tagInMs(heute);
    const b = tagInMs(datum);
    if (a === null || b === null) return null;
    return Math.round((b - a) / 86_400_000);
};

/** Leer oder nur der Platzhalter „Gelände", den die Stammdaten ohne Strasse schreiben. */
export const istAdresseLeer = (adresse: string | undefined | null): boolean => {
    const t = (adresse || '').trim();
    return !t || t === 'Gelände';
};

/** Strasse / PLZ / Ort aus der einen Adresszeile — dasselbe Muster wie ProjectMasterData. */
export const zerlegeAdresse = (adresse: string | undefined | null): { street: string; zip: string; city: string } => {
    const leer = { street: '', zip: '', city: '' };
    const s = (adresse || '').trim();
    if (!s) return leer;
    const parts = s.split(', ');
    if (parts.length >= 2) {
        const street = parts[0] === 'Gelände' ? '' : parts[0];
        const rest = parts.slice(1).join(', ').trim();
        const match = rest.match(/^(\d+)\s+(.*)$/);
        if (match) return { street, zip: match[1], city: match[2] };
        return /^\d+$/.test(rest) ? { street, zip: rest, city: '' } : { street, zip: '', city: rest };
    }
    if (s === 'Gelände') return leer;
    if (/^\d+$/.test(s)) return { street: '', zip: s, city: '' };
    const match = s.match(/^(\d+)\s+(.*)$/);
    if (match) return { street: '', zip: match[1], city: match[2] };
    return { street: s, zip: '', city: '' };
};

/** Gegenstueck zu zerlegeAdresse — identisch mit buildFullAddress in ProjectMasterData. */
export const baueAdresse = (teile: { street: string; zip: string; city: string }): string => {
    const street = teile.street.trim();
    const zip = teile.zip.trim();
    const city = teile.city.trim();
    if (street && (zip || city)) return `${street}, ${[zip, city].filter(Boolean).join(' ')}`.trim();
    if (street) return street;
    return [zip, city].filter(Boolean).join(' ').trim();
};

/** Muss das Projektbild schon da sein? Nur mit gueltigem Ende, ab 4 Wochen davor (und danach). */
export const brauchtBildPruefung = (globalData: HygieneGlobalData | undefined, heute: string): boolean => {
    if (!globalData || globalData.status === 'finished' || globalData.status === 'completed') return false;
    const tage = tageBis(heute, globalData.endDate);
    return tage !== null && tage <= BILD_WARNUNG_TAGE;
};

/** Gilt die gespeicherte Bestaetigung noch fuer das heutige Enddatum? */
export const endeBestaetigungGilt = (globalData: HygieneGlobalData | undefined, heute: string): boolean => {
    const b = globalData?.endDateConfirmation;
    if (!b || !globalData?.endDate) return false;
    if (b.endDate !== globalData.endDate) return false;
    const alter = tageBis(b.confirmedAt.slice(0, 10), heute);
    return alter !== null && alter >= 0 && alter <= BESTAETIGUNG_GUELTIG_TAGE;
};

export const findeDatenluecken = ({ metadata, globalData, heute, bildVorhanden }: HygieneEingabe): DatenLuecke[] => {
    if (globalData?.status === 'finished' || globalData?.status === 'completed') return [];
    const sifa = metadata.projectType === 'sifa';
    const luecken: DatenLuecke[] = [];

    if (!sifa) {
        const tage = tageBis(heute, globalData?.endDate);
        if (tage === null) luecken.push({ art: 'ende_fehlt' });
        else if (!endeBestaetigungGilt(globalData, heute)) {
            if (tage < 0) luecken.push({ art: 'ende_ueberschritten', tage });
            else if (tage <= ENDE_WARNUNG_TAGE) luecken.push({ art: 'ende_naht', tage });
        }
    }
    if (!(metadata.projectDescription || '').trim()) luecken.push({ art: 'beschreibung' });
    if (istAdresseLeer(metadata.projectAddress)) luecken.push({ art: 'adresse' });
    if (!sifa && !(metadata.projectCategory || metadata.category || '').trim()) luecken.push({ art: 'kategorie' });
    if (!sifa && bildVorhanden === false && brauchtBildPruefung(globalData, heute)) luecken.push({ art: 'bild' });

    return luecken;
};

// ─── „Später erinnern" — nur fuer diese Sitzung ─────────────────────────────

interface StorageLike {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
}

const snoozeKey = (projectId: string) => `hygiene.snooze.${projectId}`;

export const istZurueckgestellt = (storage: StorageLike | undefined, projectId: string | undefined): boolean => {
    if (!storage || !projectId) return false;
    try { return storage.getItem(snoozeKey(projectId)) === '1'; } catch { return false; }
};

export const stelleZurueck = (storage: StorageLike | undefined, projectId: string | undefined): void => {
    if (!storage || !projectId) return;
    try { storage.setItem(snoozeKey(projectId), '1'); } catch { /* Private Mode/Quota — dann fragt es eben nochmal */ }
};
